import type { Dispatch, SetStateAction } from 'react';
import { FileText } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@respectly/ui';
import { SignerInfoPanel } from './SignerInfoPanel';
import {
  SignerFieldList,
  type SignerFieldListField,
  type SignerFieldValue,
} from './SignerFieldList';

interface DrawerDocumentData {
  document: {
    name: string;
    pageCount: number;
    signatureFields: SignerFieldListField[];
  };
  contact: {
    firstName: string;
    lastName?: string;
    email?: string;
  };
  expiresAt?: string;
  signerContext?: {
    role: string;
    order: number;
    totalSigners: number;
  };
}

interface MobileSigningDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documentData: DrawerDocumentData;
  signedFields: Map<string, SignerFieldValue>;
  setSignedFields: Dispatch<SetStateAction<Map<string, SignerFieldValue>>>;
  onSignatureFieldClick: (field: SignerFieldListField) => void;
}

export function MobileSigningDrawer({
  open,
  onOpenChange,
  documentData,
  signedFields,
  setSignedFields,
  onSignatureFieldClick,
}: MobileSigningDrawerProps) {
  const requiredCount = documentData.document.signatureFields.filter(
    (f) => f.required
  ).length;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="bottom" showCloseButton={false} maxHeight="88vh">
        <div className="flex flex-shrink-0 flex-col items-center pt-2 pb-1">
          <div className="h-1 w-10 rounded-full bg-gray-300" aria-hidden />
        </div>

        <div className="flex flex-shrink-0 items-center gap-3 border-b border-gray-100 px-5 py-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100">
            <FileText className="h-5 w-5 text-primary-600" />
          </div>
          <div className="min-w-0 flex-1">
            <SheetTitle className="truncate text-base text-gray-900">
              {documentData.document.name}
            </SheetTitle>
            <SheetDescription className="text-xs text-gray-500">
              {documentData.document.pageCount} page
              {documentData.document.pageCount > 1 ? 's' : ''}
            </SheetDescription>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          <div className="flex flex-col gap-4">
            <SignerInfoPanel
              hideDocumentCard
              document={documentData.document}
              contact={documentData.contact}
              signerContext={documentData.signerContext}
              signedCount={signedFields.size}
              requiredCount={requiredCount}
              totalFields={documentData.document.signatureFields.length}
            />

            <SignerFieldList
              fields={documentData.document.signatureFields}
              signedFields={signedFields}
              setSignedFields={setSignedFields}
              onSignatureFieldClick={onSignatureFieldClick}
            />

            {documentData.expiresAt && (
              <p className="pt-2 text-center text-xs text-gray-400">
                This link expires on{' '}
                {new Date(documentData.expiresAt).toLocaleDateString()}
              </p>
            )}
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
